import React from 'react';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _map from 'lodash/map';
import _findIndex from 'lodash/findIndex';
import _reject from 'lodash/reject';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
// Core components
import Box from 'components/core/Box';
import MenuButton from 'components/core/MenuButton';
// Models
import { virtualTriggerEthernet } from 'models/device';
import mEquipment from 'models/equipment';
// Custom
import { deviceSlaveTriggerDataShape, menuItemsShape } from 'constants/PropTypesShapes';
import { SubFormRefContext } from 'components/misc/Contexts';
import { AddEquipmentTextButton } from 'components/buttons/TextButtons';
import { showDeleteMenuItems } from 'utils/MenuItems';
import InformationItem from 'components/items/InformationItem';
import withScrollerManagement from 'hocs/WithScrollerManagement';
import ConfigureDeviceSlaveTriggerEthernetAddEditForm from './ConfigureDeviceSlaveTriggerEthernetAddEditForm';

const EquipmentItem = ({ equipment, active, menuItems }) => (
  <Box type="highlightable" highlighted={active}>
    <InformationItem
      label={(
        <div>
          <div>
            {`${equipment.mac} - ${equipment.ip}`}
          </div>
          <div>
            {`${equipment.brand} - ${equipment.model}`}
          </div>
        </div>
      )}
      value={equipment.name}
      icon={mEquipment.type(equipment).icon}
      rightButton={<MenuButton obj={equipment} items={menuItems} />}
    />
  </Box>
);

EquipmentItem.propTypes = {
  equipment: PropTypes.shape({
    type: PropTypes.string.isRequired,
    brand: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    ip: PropTypes.string.isRequired,
    mac: PropTypes.string.isRequired,
  }).isRequired,
  active: PropTypes.bool,
  menuItems: menuItemsShape.isRequired,
};

EquipmentItem.defaultProps = {
  active: false,
};

class ConfigureDeviceSlaveTriggerEthernet extends React.Component {
  constructor(props) {
    super(props);

    this.handleAdd = this.handleAdd.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleCancelAddEditEquipment = this.handleCancelAddEditEquipment.bind(this);
    this.handleValidateAddEditEquipment = this.handleValidateAddEditEquipment.bind(this);

    this.indexEditTrigger = -1;
    this.state = {
      editTrigger: null,
      addEditFormVisible: false,
      addEditFormErrors: null,
    };
  }

  // Handlers
  handleAdd() {
    this.doShowAddEditForm();
  }

  handleEdit(equipment) {
    const { triggers } = this.props;
    const index = _findIndex(triggers, ['equipment.mac', equipment.mac]);
    if (index !== -1) {
      this.indexEditTrigger = index;
      this.setState({
        editTrigger: triggers[index],
      }, this.doShowAddEditForm);
    }
  }

  handleCancelAddEditEquipment() {
    this.indexEditTrigger = -1;
    this.setState({
      editTrigger: null,
      addEditFormVisible: false,
      addEditFormErrors: null,
    });
  }

  handleValidateAddEditEquipment(equipment) {
    const { triggers } = this.props;

    // Check IP and MAC (must be unique)
    const index1 = _findIndex(triggers, ['equipment.mac', equipment.mac]);
    const index2 = _findIndex(triggers, ['equipment.ip', equipment.ip]);
    if (index1 !== -1 && index1 !== this.indexEditTrigger) {
      this.setState({
        addEditFormErrors: { global: 'duplicateMac' },
      });
    } else if (index2 !== -1 && index2 !== this.indexEditTrigger) {
      this.setState({
        addEditFormErrors: { global: 'duplicateIp' },
      });
    } else {
      let newTriggers;
      if (this.indexEditTrigger > -1) {
        // Edit trigger
        newTriggers = _map(triggers, (trigger, index) => (index === this.indexEditTrigger ? {
          ...trigger,
          equipment,
        } : trigger));
      } else {
        newTriggers = [...triggers, {
          ...virtualTriggerEthernet,
          equipment,
        }];
      }
      this.indexEditTrigger = -1;
      this.setState({
        editTrigger: null,
        addEditFormVisible: false,
        addEditFormErrors: null,
      }, () => this.doUpdateEquipments(newTriggers));
    }
  }

  handleDelete(equipment) {
    const { triggers } = this.props;
    this.indexEditTrigger = -1;
    this.setState({
      editTrigger: null,
      addEditFormVisible: false,
      addEditFormErrors: null,
    }, () => this.doUpdateEquipments(_reject(triggers, ['equipment.mac', equipment.mac])));
  }

  // Private
  doShowAddEditForm() {
    this.setState({
      addEditFormVisible: true,
      addEditFormErrors: null,
    }, () => {
      const { handleScrollTo } = this.props;
      handleScrollTo('confDevSlaTriEthAddEdit', 'subForm');
    });
  }

  doUpdateEquipments(triggers) {
    const { onTriggerChange } = this.props;
    onTriggerChange(triggers);
  }

  render() {
    const { triggers } = this.props;
    const { editTrigger, addEditFormVisible, addEditFormErrors } = this.state;

    return (
      <div className="box box-default m-0">
        <div className="box-header box-dark">
          <LocalizedMessage id="info.deviceSlaveTriggerEquipmentEthernet" />
        </div>
        <div className="row m-0 pb-3">
          {_map(triggers, ({ equipment }) => (
            equipment && (
              <div key={equipment.mac} className="col-xl-4 col-sm-6 col-xs-12">
                <EquipmentItem
                  equipment={equipment}
                  active={_get(editTrigger, 'equipment.mac') === equipment.mac}
                  menuItems={showDeleteMenuItems({ cb: this.handleEdit }, { cb: this.handleDelete })}
                />
              </div>
            )
          ))}
          <SubFormRefContext.Provider value="confDevSlaTriEthAddEdit">
            {addEditFormVisible && (
              <div className="col-12 pt-3">
                <ConfigureDeviceSlaveTriggerEthernetAddEditForm
                  equipment={_get(editTrigger, 'equipment')}
                  parentErrors={addEditFormErrors}
                  onCancel={this.handleCancelAddEditEquipment}
                  onValidate={this.handleValidateAddEditEquipment}
                />
              </div>
            )}
          </SubFormRefContext.Provider>
          {!addEditFormVisible && (
            <div className="col-12 pt-3">
              <AddEquipmentTextButton primary onClick={this.handleAdd} />
            </div>
          )}
        </div>
      </div>
    );
  }
}

ConfigureDeviceSlaveTriggerEthernet.propTypes = {
  triggers: PropTypes.arrayOf(deviceSlaveTriggerDataShape).isRequired,
  onTriggerChange: PropTypes.func.isRequired,
  handleScrollTo: PropTypes.func.isRequired,
};

module.exports = withScrollerManagement(ConfigureDeviceSlaveTriggerEthernet);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/assets/configuration/ConfigureDeviceSlaveTriggerEthernet.js